/* eslint-disable no-restricted-syntax */
import React, { memo, useState, useEffect, ChangeEvent } from 'react';
import moment from 'moment';
import { useIntl } from 'react-intl';
import { Grid, Theme } from '@material-ui/core';
import AppBar from '@material-ui/core/AppBar';
import { withStyles, WithStyles, createStyles } from '@material-ui/core/styles';
import { AutocompleteChangeReason } from '@material-ui/lab';
import { ActionModal } from '../../../../components/Modal';
import { TextField } from '../../../../components/TextField';
import { TimePicker } from '../../../../components/TimePicker';
import { Button } from '../../../../components/Button';
import { generateRoomWithoutSeparator } from '../../../../helper/random';
import { isLowercaseAlphanumeric } from '../../../../helper/validation';
import { replaceRoomNameWithTitle } from '../../../../app/config';
import {
  IGetGroupResponse,
  IGroup,
  emptyGroup,
} from '../../../group/groups.slice';
import { IDevice } from '../../../devices/devices.slice';
import { IUser } from '../../../auth/auth.slice';
import { IScheduledMeetingData } from '../../replaystreaming.api';
import { IScheduleMeetings } from '../../../scheduleMeeting/scheduleMeeting.slice';
import { AutocompleteTextField } from '../../../../components/AutocompleteTextField';
import { Checkbox } from '../../../../components/Checkbox';
import { IStreamingUrl } from '../../replaystreaming.slice';

const styles = (theme: Theme) => {
  return createStyles({
    root: {},
    datePicker: {
      margin: theme.spacing(2, 0),
      '& .MuiFormControl-root': {
        width: '100%',
      },
    },
    select: {
      width: '100%',
      color: '#989898',
      margin: '10px 0',
      '& em': {
        fontStyle: 'unset !important',
      },
      '& .MuiInputLabel-outlined': {
        transform: 'translate(14px, 30px) scale(1)',
      },
      '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
        transform: 'translate(14px, -6px) scale(0.75)',
      },
    },
    appbar: {
      backgroundColor: theme.palette.grey[100],
      boxShadow: 'none',
      borderRadius: 8,
      zIndex: 100,
      margin: '28px 0',
    },
    borderRight: {
      '& .MuiTab-wrapper': {
        borderRight: `1px solid ${theme.palette.grey[500]}`,
      },
    },
    borderLeft: {
      '& .MuiTab-wrapper': {
        borderLeft: `1px solid ${theme.palette.grey[500]}`,
      },
    },
    autocomplete: {
      '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"]': {
        padding: '0',
      },
    },
    streamerDescription: {
      marginTop: '12px',
      height: '103px',
    },
  });
};

export interface IScheduleMeetingModalProps extends WithStyles<typeof styles> {
  open: boolean;
  isUpdateReplaystreaming: boolean;
  meeting: IScheduleMeetings;
  groups: IGetGroupResponse;
  devicesList: IDevice[];
  membersList: IUser[];
  handleClose?: React.MouseEventHandler<HTMLButtonElement>;
  saveMeetingButtonClick: (data: IScheduledMeetingData) => void;
  loading: boolean;
  isCreateReplayStreaming: boolean;
  isViewReplayStreaming: boolean;
  streamingUrlsList: IStreamingUrl[];
}

const ScheduleReplayStreamingModal = ({
  classes,
  open,
  isUpdateReplaystreaming,
  meeting,
  groups,
  devicesList,
  membersList,
  handleClose,
  saveMeetingButtonClick,
  loading,
  isCreateReplayStreaming,
  isViewReplayStreaming,
  streamingUrlsList,
}: IScheduleMeetingModalProps) => {
  const intl = useIntl();
  const [meetingName, setMeetingName] = useState({ value: '', error: '' });
  const [startTime, setStartTime] = useState({
    value: moment(new Date()).format('YYYY-MM-DDTHH:mm'),
    error: '',
  });
  const endDate = new Date();
  endDate.setTime(endDate.getTime() + 60 * 60 * 1000);
  const [endTime, setEndTime] = useState({
    value: moment(endDate).format('YYYY-MM-DDTHH:mm'),
    error: '',
  });
  const [tabIndex, setTabIndex] = React.useState(0);

  interface IMemberState {
    error: boolean;
    value: IUser[];
  }
  interface IDeviceState {
    error: boolean;
    value: IDevice | null;
  }

  interface IStreamingUrlState {
    error: boolean;
    value: IStreamingUrl | null;
  }

  const [members, setMembers] = useState<IMemberState>({
    error: false,
    value: [],
  });
  const [location, setLocation] = useState({ value: '', error: '' });
  const [group, setGroup] = useState({
    value: {
      ...emptyGroup,
      name: '',
    },
    error: '',
  });
  const [devices, setDevices] = useState<IDeviceState>({
    error: false,
    value: null,
  });
  const [streamingUrl, setStreamingUrl] = useState<IStreamingUrlState>({
    error: false,
    value: null,
  });

  const [autoStartStreaming, setAutoStartStreaming] = useState(false);
  const [showInFullScreen, setShowInFullScreen] = useState(false);
  const [repeatUntilEndTime, setRepeatUntilEndTime] = useState(false);
  const [dontSaveOnYoutube, setDontSaveOnYoutube] = useState(false);

  useEffect(() => {
    if ((isUpdateReplaystreaming || isViewReplayStreaming) && meeting) {
      const startTimeError =
        new Date() > new Date(meeting?.beginTime)
          ? intl.formatMessage({
              id: 'enter_valid_date_time',
            })
          : '';
      const endTimeError =
        new Date() > new Date(meeting?.endTime)
          ? intl.formatMessage({
              id: 'enter_valid_date_time',
            })
          : '';

      setMeetingName({ value: meeting?.name, error: '' });
      setLocation({ value: meeting?.location, error: '' });
      setStartTime({
        value: moment(meeting?.beginTime).format('YYYY-MM-DDTHH:mm'),
        error: startTimeError,
      });
      setEndTime({
        value: moment(meeting?.endTime).format('YYYY-MM-DDTHH:mm'),
        error: endTimeError,
      });
      if (meeting?.groupId) setGroup({ value: meeting.groupId, error: '' });

      const setMembersValueArr = [];
      for (const member1 of meeting?.members) {
        for (const member2 of membersList) {
          if (member1.userId._id === member2._id) {
            setMembersValueArr.push(member2);
          }
        }
      }
      setMembers({
        value: setMembersValueArr,
        error: setMembersValueArr.length === 0,
      });
      const setDevicesValueArr = [];
      for (const device1 of meeting?.devices) {
        for (const device2 of devicesList) {
          if (device1.deviceId._id === device2._id) {
            setDevicesValueArr.push(device2);
          }
        }
      }
      setDevices({
        value: setDevicesValueArr[0] || null,
        error: setDevicesValueArr.length === 0,
      });

      const setReplaystreamingDevicesValueArr = [];
      for (const device1 of meeting?.livestreamingdevices) {
        for (const device2 of devicesList) {
          if (device1.deviceId._id === device2._id) {
            setReplaystreamingDevicesValueArr.push(device2);
          }
        }
      }

      const streamingUrlItem = streamingUrlsList.find(
        (url) =>
          url.livestreaming.broadcastId === meeting.replaystreaming.broadcastId
      );
      setStreamingUrl({ value: streamingUrlItem || null, error: false });
    }
  }, [
    isUpdateReplaystreaming,
    isViewReplayStreaming,
    membersList,
    devicesList,
    meeting,
  ]);

  const _handleNameChange = (e: { target: { value: string } }) => {
    if (replaceRoomNameWithTitle) {
      setMeetingName({ value: e.target.value, error: '' });
      setLocation({ value: e.target.value, error: '' });
    } else if (!replaceRoomNameWithTitle) {
      setMeetingName({ value: e.target.value, error: '' });
    }
  };

  const _handleLocationChange = (e: { target: { value: string } }) => {
    if (isLowercaseAlphanumeric(e.target.value))
      setLocation({ value: e.target.value, error: '' });
  };

  const _generateRoomName = () => {
    const roomName = generateRoomWithoutSeparator();
    setLocation({ value: roomName, error: '' });
  };

  const _handleStartTimeChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    if (new Date() <= new Date(e.target.value)) {
      setStartTime({ value: e.target.value, error: '' });
      const date = new Date(e.target.value);
      date.setTime(date.getTime() + 60 * 60 * 1000); // set end time 1 hour after start time
      setEndTime({ value: moment(date).format('YYYY-MM-DDTHH:mm'), error: '' });
    } else
      setStartTime({
        value: e.target.value,
        error: intl.formatMessage({
          id: 'enter_valid_date_time',
        }),
      });
  };

  const handleMemberChange = (
    event: ChangeEvent<unknown>,
    value: IUser[],
    reason: AutocompleteChangeReason
  ) => {
    setMembers({
      value,
      error: value.length === 0,
    });
  };

  const _handleEndTimeChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (new Date() <= new Date(e.target.value))
      setEndTime({ value: e.target.value, error: '' });
    else
      setEndTime({
        value: e.target.value,
        error: intl.formatMessage({
          id: 'enter_valid_date_time',
        }),
      });
  };

  // const _handleParticipantsChange = (e: any) => {
  //   setParticipants({ value: e.target.value, error: '' });
  // };

  const handleGroupChange = (
    event: ChangeEvent<unknown>,
    value: IGroup | null,
    reason: AutocompleteChangeReason
  ) => {
    if (value) {
      setGroup({
        value,
        error: '',
      });
    } else {
      setGroup({
        value: {
          ...emptyGroup,
          name: '',
        },
        error: '',
      });
    }
  };

  const handleDeviceChange = (
    event: ChangeEvent<unknown>,
    value: IDevice | null,
    reason: AutocompleteChangeReason
  ) => {
    setDevices({
      value,
      error: !value,
    });
  };

  const handleStreamingUrlChange = (
    event: ChangeEvent<unknown>,
    value: any,
    reason: AutocompleteChangeReason
  ) => {
    setStreamingUrl({
      value,
      error: !value,
    });
  };

  const _validate = () => {
    let isValidate = true;
    if (!meetingName.value) {
      isValidate = false;
      setMeetingName({
        ...meetingName,
        error: intl.formatMessage({
          id: 'meeting_name_required',
        }),
      });
    }

    if (startTime.error || endTime.error) isValidate = false;

    if (new Date(startTime.value) > new Date(endTime.value)) {
      isValidate = false;
      setEndTime({
        ...endTime,
        error: intl.formatMessage({
          id: 'end_time_cannot_be_before_begin_time',
        }),
      });
    }

    if (!streamingUrl.value) {
      isValidate = false;
      setStreamingUrl({
        ...streamingUrl,
        error: true,
      });
    }

    return isValidate;
  };

  const _saveMeetingButtonClick = () => {
    if (_validate()) {
      const memberIdsArr = [];
      const deviceIdsArr = [];
      // eslint-disable-next-line no-restricted-syntax
      for (const member of members.value) {
        memberIdsArr.push(member._id);
      }
      // Changed device handling for single device
      if (devices.value) {
        deviceIdsArr.push(devices.value._id);
      }

      let data: IScheduledMeetingData = {
        name: meetingName.value,
        beginTime: new Date(startTime.value),
        endTime: new Date(endTime.value),
        participantCount: 0,
        location: location.value,
        groupId: group.value._id || null,
        deviceId: null,
        members: memberIdsArr,
        livestreamingGroupId: null,
        devices: deviceIdsArr,
        livestreamingDevices: [],
        replayStreamingId:
          streamingUrl.value?.livestreaming?.broadcastId || null,
      };

      if (isUpdateReplaystreaming) data = { ...data, id: meeting?._id };

      saveMeetingButtonClick(data);
    }
  };

  const handleChange = (event: React.ChangeEvent<any>, newValue: any) => {
    setTabIndex(newValue);
  };

  return (
    <ActionModal
      modalTitle={intl.formatMessage({
        id:
          (isCreateReplayStreaming && 'create_replaystream') ||
          (isUpdateReplaystreaming && 'update_replaystream') ||
          'view_replaystream',
      })}
      modalDescription={intl.formatMessage({
        id:
          isUpdateReplaystreaming || isCreateReplayStreaming
            ? 'create_replaystream_description'
            : 'view_replaystream_description',
      })}
      id="create-replaystream-modal"
      open={open}
      handleClose={handleClose}
      actinButtonOnClick={_saveMeetingButtonClick}
      actinButtonLabel={intl.formatMessage({
        id: isCreateReplayStreaming ? 'create' : 'save',
      })}
      actionButtonDisabled={
        loading || !(isUpdateReplaystreaming || isCreateReplayStreaming)
      }
    >
      <TextField
        placeholder={intl.formatMessage({
          id: 'title',
        })}
        onChange={_handleNameChange}
        value={meetingName.value}
        required
        error={Boolean(meetingName.error)}
        helperText={meetingName.error}
        disabled={!(isUpdateReplaystreaming || isCreateReplayStreaming)}
      />
      {!replaceRoomNameWithTitle && (
        <TextField
          placeholder="Add room name"
          onChange={_handleLocationChange}
          value={location.value}
          error={Boolean(location.error)}
          helperText={location.error}
          disabled={!(isUpdateReplaystreaming || isCreateReplayStreaming)}
        />
      )}
      {!replaceRoomNameWithTitle &&
        !(isUpdateReplaystreaming || isCreateReplayStreaming) && (
          <Button
            color="primary"
            id="auto generate meeting-room"
            variant="text"
            label="Or generate meeting name"
            onClick={_generateRoomName}
            // customStyles={classes.tableActionButton}
          />
        )}
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TimePicker
            label={intl.formatMessage({
              id: 'start_time',
            })}
            type="datetime-local"
            id="schedule-meeting-start-time"
            onChange={(e) => _handleStartTimeChange(e)}
            value={startTime.value}
            error={!isViewReplayStreaming ? Boolean(startTime.error) : false}
            helperText={!isViewReplayStreaming ? startTime.error : null}
            disabled={!(isUpdateReplaystreaming || isCreateReplayStreaming)}
          />
        </Grid>
        <Grid item xs={6}>
          <TimePicker
            label={intl.formatMessage({
              id: 'end_time',
            })}
            type="datetime-local"
            id="schedule-meeting-end-time"
            onChange={(e) => _handleEndTimeChange(e)}
            value={endTime.value}
            error={!isViewReplayStreaming ? Boolean(endTime.error) : false}
            helperText={!isViewReplayStreaming ? endTime.error : null}
            disabled={!(isUpdateReplaystreaming || isCreateReplayStreaming)}
          />
        </Grid>
      </Grid>

      <AutocompleteTextField
        multiple={false}
        limitTags={1}
        id="replay-streaming-url"
        options={streamingUrlsList}
        getOptionLabel={(option) => option.name}
        value={streamingUrl.value}
        getOptionSelected={(option, value) => option.id === value.id}
        onChange={handleStreamingUrlChange}
        disabled={!(isUpdateReplaystreaming || isCreateReplayStreaming)}
        label={intl.formatMessage({
          id: 'streaming_url',
        })}
        placeholder={intl.formatMessage({
          id: 'select_streaming_url',
        })}
        error={Boolean(streamingUrl.error)}
        helperText={intl.formatMessage({
          id: 'streaming_url_required',
        })}
        required
      />

      <AutocompleteTextField
        multiple={false}
        limitTags={1}
        id="schedule-meeting-groups"
        options={groups.data}
        getOptionLabel={(option) => option.name}
        value={group.value}
        getOptionSelected={(option, value) => option.id === value.id}
        onChange={handleGroupChange}
        disabled={!(isUpdateReplaystreaming || isCreateReplayStreaming)}
        label={intl.formatMessage({
          id: 'group',
        })}
        placeholder={intl.formatMessage({
          id: 'select_a_group',
        })}
      />
      <AutocompleteTextField
        multiple
        limitTags={2}
        id="schedule-meeting-members"
        options={membersList}
        getOptionLabel={(option) => option.profile.name}
        value={members.value}
        getOptionSelected={(option, value) => option.id === value.id}
        onChange={handleMemberChange}
        disabled={!(isUpdateReplaystreaming || isCreateReplayStreaming)}
        label={intl.formatMessage({
          id: 'participants',
        })}
      />

      <AutocompleteTextField
        multiple={false}
        limitTags={1}
        id="schedule-meeting-devices"
        options={devicesList}
        getOptionLabel={(option) => option.userDeviceName || option.deviceName}
        value={devices.value}
        getOptionSelected={(option, value) => option.id === value.id}
        onChange={handleDeviceChange}
        disabled={!(isUpdateReplaystreaming || isCreateReplayStreaming)}
        label={intl.formatMessage({
          id: 'devices',
        })}
      />

      {/* <Checkbox
        id="auto-start-streaming"
        checked={autoStartStreaming}
        onChange={() => setAutoStartStreaming(!autoStartStreaming)}
        label={intl.formatMessage({
          id: 'start_streaming_on_time_automatically',
        })}
      />
      <br />
      <Checkbox
        id="show-in-full-screen"
        checked={showInFullScreen}
        onChange={() => setShowInFullScreen(!showInFullScreen)}
        label={intl.formatMessage({
          id: 'show_in_full_screen',
        })}
      />
      <br />
      <Checkbox
        id="repeat-until-end-time"
        checked={repeatUntilEndTime}
        onChange={() => setRepeatUntilEndTime(!repeatUntilEndTime)}
        label={intl.formatMessage({
          id: 'repeat_until_end_time',
        })}
      />
      <br />
      <Checkbox
        id="dont-save-on-youtube"
        checked={dontSaveOnYoutube}
        onChange={() => setDontSaveOnYoutube(!dontSaveOnYoutube)}
        label={intl.formatMessage({
          id: 'dont_save_on_youtube',
        })}
      /> */}
    </ActionModal>
  );
};

export default memo(withStyles(styles)(ScheduleReplayStreamingModal));
